import React, { useId } from 'react';

const SEASONS = [
    { id: 'spring', label: 'Spring' },
    { id: 'summer', label: 'Summer' },
    { id: 'fall', label: 'Fall' },
];

/**
 * Multi-select chip picker for harvest / bloom seasons.
 *
 * @param {Object}   props
 * @param {string[]} props.value     - Currently selected season IDs, e.g. ['spring', 'fall']
 * @param {Function} props.onChange  - Called with the new string[] when selection changes
 * @param {string}   [props.label]   - Override the field label (defaults to "Harvest Season")
 * @param {boolean}  [props.isFlower] - When true, shows "Bloom Season" as the label
 * @param {boolean}  [props.required] - When true, at least one season must remain selected
 * @param {string}   [props.className] - Extra class for the outer wrapper
 * @param {string}   [props.size]    - 'sm' for compact variant (used in forms with tight grids)
 */
const SeasonPicker = ({
    value = [],
    onChange,
    label,
    isFlower = false,
    required = false,
    className = '',
    size = 'md',
}) => {
    const resolvedLabel = label ?? (isFlower ? 'Bloom Season' : 'Harvest Season');
    const isSmall = size === 'sm';

    const toggle = (seasonId) => {
        const next = value.includes(seasonId)
            ? value.filter((s) => s !== seasonId)
            : [...value, seasonId];

        // Enforce at least one selection when required
        if (required && next.length === 0) return;

        onChange(next);
    };

    const uid = useId();
    const groupId = `season-picker-${uid}`;

    return (
        <div className={className} role="group" aria-labelledby={groupId}>
            <span id={groupId} className={`block font-semibold text-[#2F5233] mb-1 ${isSmall ? 'text-xs' : 'text-sm'}`}>
                {resolvedLabel}
            </span>
            <div className="flex flex-wrap gap-1.5">
                {SEASONS.map((s) => {
                    const selected = value.includes(s.id);
                    return (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => toggle(s.id)}
                            aria-pressed={selected}
                            className={[
                                'font-medium transition-colors border-2 rounded-lg',
                                isSmall ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
                                selected
                                    ? 'bg-[#52A675] border-[#52A675] text-white'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-[#52A675] hover:text-[#2F5233]',
                            ].join(' ')}
                        >
                            {s.label}
                        </button>
                    );
                })}
            </div>
            {required && value.length === 0 && (
                <p className="mt-1 text-xs text-red-500">Please select at least one season.</p>
            )}
        </div>
    );
};

export default SeasonPicker;
